import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderId = searchParams.get('order_id');
    const mpStatus = searchParams.get('status');
    const paymentId = searchParams.get('payment_id');
    
    // Mercado Pago retorna com order_id e status
    if (orderId) {
      // Se veio payment_id, força verificação no backend (atualiza DB mesmo sem webhook)
      if (paymentId) {
        verifyMercadoPagoPayment(paymentId, orderId);
        return;
      }

      // Caso não venha payment_id, tenta consultar o pedido e fazer polling até atualizar
      if (mpStatus === 'pending') {
        setStatus('pending');
        pollOrderStatus(orderId);
      } else {
        pollOrderStatus(orderId);
      }
      return;
    }
    
    // Fallback para session_id (modo simulado)
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const verifyMercadoPagoPayment = async (paymentId, orderId, attempt = 0) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API}/mercadopago/verify?payment_id=${encodeURIComponent(paymentId)}`, { withCredentials: true });
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      if (response.data.payment_status === 'failed') {
        setStatus('error');
        return;
      }
      setStatus('pending');
      setAttempts(attempt + 1);
      setTimeout(() => verifyMercadoPagoPayment(paymentId, orderId, attempt + 1), 2000);
    } catch (error) {
      console.error('Error verifying Mercado Pago payment:', error);
      // fallback: tenta pelo pedido
      pollOrderStatus(orderId, attempt);
    }
  };

  const pollOrderStatus = async (orderId, attempt = 0) => {
    if (attempt >= 10) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      if (response.data.payment_status === 'failed') {
        setStatus('error');
        return;
      }
      setStatus('pending');
      setAttempts(attempt + 1);
      setTimeout(() => pollOrderStatus(orderId, attempt + 1), 2000);
    } catch (error) {
      console.error('Error polling order status:', error);
      setStatus('error');
    }
  };

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(attempt + 1);
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Verificando Pagamento...</h2>
          <p className="text-slate-600 dark:text-slate-300">Por favor, aguarde</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <div className="text-center max-w-md">
          <Loader2 className="h-24 w-24 text-yellow-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">Pagamento Pendente</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-8">
            Seu pagamento está sendo processado. Você receberá uma confirmação assim que for aprovado.
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/orders')} data-testid="view-orders-btn">Ver Meus Pedidos</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center" data-testid="checkout-success">
        <div className="text-center max-w-md">
          <CheckCircle className="h-24 w-24 text-green-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">Pedido Confirmado!</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-8">
            Seu pedido foi realizado com sucesso. Em breve você receberá um e-mail com os detalhes.
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/orders')} data-testid="view-orders-btn">Ver Meus Pedidos</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Voltar ao Início</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
      <div className="text-center max-w-md">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">Ops!</h1>
        <p className="text-slate-600 dark:text-slate-300 mb-8">
          Não foi possível verificar o status do pagamento. Por favor, verifique seus pedidos.
        </p>
        <Button onClick={() => navigate('/orders')}>Ver Meus Pedidos</Button>
      </div>
    </div>
  );
};